import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import { resolveAssetUrl } from "@/lib/assetUrl";

/**
 * PhotosTabPanel — public gallery grid + (when owner/admin) a two-tap
 * drag-and-drop uploader that writes to `business.gallery_images`.
 * Reuses the shared `/api/upload/image?image_type=business` endpoint so
 * uploaded files land in Object Storage like every other asset.
 */
export default function PhotosTabPanel({ business, canManage, onSaved }) {
  const bizId = business?.business_id;
  const [images, setImages] = useState(() => Array.isArray(business?.gallery_images) ? [...business.gallery_images] : []);
  const [uploading, setUploading] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const persist = useCallback(async (next) => {
    try {
      const r = await axios.put(
        `${API}/businesses/${bizId}/gallery-images`,
        { gallery_images: next },
        { withCredentials: true },
      );
      const saved = r.data?.gallery_images || next;
      setImages(saved);
      onSaved?.(saved);
      return saved;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save gallery");
      throw e;
    }
  }, [bizId, onSaved]);

  const uploadOne = useCallback(async (file) => {
    const form = new FormData();
    form.append("file", file);
    const r = await axios.post(
      `${API}/upload/image?image_type=business`,
      form,
      { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } },
    );
    return r.data?.image_url;
  }, []);

  const handleFiles = useCallback(async (files) => {
    if (!files || !files.length) return;
    const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!list.length) {
      toast.error("Please choose image files (PNG or JPG).");
      return;
    }
    setUploading(list.length);
    const uploaded = [];
    for (const f of list) {
      try {
        const url = await uploadOne(f);
        if (url) uploaded.push(url);
      } catch (e) {
        toast.error(`Couldn't upload ${f.name}`);
      }
      setUploading((n) => Math.max(0, n - 1));
    }
    if (uploaded.length) {
      const next = [...images, ...uploaded].slice(0, 24);
      await persist(next);
      toast.success(`${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} added`);
    }
  }, [images, persist, uploadOne]);

  const removeAt = useCallback(async (idx) => {
    const next = images.filter((_, i) => i !== idx);
    await persist(next);
  }, [images, persist]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (canManage) handleFiles(e.dataTransfer.files);
  };

  if (!images.length && !canManage) {
    return (
      <div className="rounded-2xl border border-dashed border-tbc-gold/40 bg-tbc-cream/40 p-10 text-center" data-testid="photos-empty">
        <ImageIcon className="w-10 h-10 mx-auto text-tbc-gold" strokeWidth={1.3} />
        <p className="mt-3 font-heading text-lg text-tbc-navy">No photos yet</p>
        <p className="mt-1 text-sm text-gray-500 font-body">
          Check back soon — the owner will drop shots here.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="photos-tab-panel">
      {canManage && (
        <label
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={
            "block cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center transition-colors " +
            (dragOver ? "border-tbc-gold bg-tbc-cream" : "border-tbc-gold/40 bg-tbc-cream/40 hover:bg-tbc-cream")
          }
          data-testid="photos-dropzone"
        >
          <input
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="photos-upload-input"
          />
          <div className="flex flex-col items-center gap-1.5">
            {uploading > 0 ? (
              <Loader2 className="w-6 h-6 text-tbc-gold animate-spin" />
            ) : (
              <Upload className="w-6 h-6 text-tbc-gold" strokeWidth={1.6} />
            )}
            <p className="font-heading text-sm text-tbc-navy">
              {uploading > 0 ? `Uploading ${uploading}…` : "Drag photos here or tap to upload"}
            </p>
            <p className="text-xs text-gray-500 font-body">PNG / JPG · up to 24 total</p>
          </div>
        </label>
      )}

      {images.length > 0 && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {images.map((src, i) => (
            <div key={`gal-${i}`} className="relative aspect-square rounded-xl overflow-hidden bg-tbc-cream group" data-testid={`gallery-photo-${i}`}>
              <img
                src={resolveAssetUrl(src) || ""}
                alt={`${business.name} gallery ${i + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {canManage && (
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  aria-label="Remove photo"
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                  data-testid={`gallery-remove-${i}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
